import { useEffect, useMemo, useState } from "react";
import { Box, Skeleton } from "@mui/material";
import { extractImageColors } from "src/utils/Utils.js";
import { registerCachedImage } from "src/utils/imageUrl.js";
import {
  PENDING_LOGO_FILTER,
  contrastSafeGradientStops,
  logoHaloFilter,
} from "src/utils/logoTreatment.js";
import { useQuery } from "@tanstack/react-query";

/** How long a candidate may sit with neither onLoad nor onError before it is
 *  treated as failed (hung service worker / stalled CDN response). */
const IMAGE_LOAD_TIMEOUT_MS = 3000;

/**
 * Derives the painted gradient and the logo's filter. Full-bleed art covers the
 * ramp and carries no mark, so it gets neither the correction nor a halo.
 *
 * @param {{ surface?: { raw: string[], ink: object | null }, coversTheRamp: boolean,
 *   grayscale?: number }} params
 * @returns {{ bgGradient: string | undefined, filterImg: string }}
 */
export const eventSurface = ({ surface, coversTheRamp, grayscale = 0 }) => {
  if (!surface) return { bgGradient: undefined, filterImg: PENDING_LOGO_FILTER };
  if (coversTheRamp) {
    return {
      bgGradient: `linear-gradient(180deg, ${surface.raw.join(", ")})`,
      filterImg: "none",
    };
  }
  const { stops } = contrastSafeGradientStops(surface.raw, surface.ink, { grayscale });
  return {
    bgGradient: `linear-gradient(180deg, ${stops.join(", ")})`,
    filterImg: logoHaloFilter(stops, surface.ink, { grayscale }),
  };
};

const EventProfileBg = ({
  profileImage,
  eventSummary,
  loading,
  backgroundMidHeight,
  backgroundFullHeight,
  fill,
  sx,
  handleImageError,
  grayscale = 0,
  ...props
}) => {
  const [loadedProfileImage, setLoadedProfileImage] = useState(null);
  const imageLoaded = Boolean(profileImage) && loadedProfileImage === profileImage;

  // Gated like the watchdog below: while loading, the painted source is still
  // awaiting its processed derivative, so no cached verdict is actionable yet.
  const registerImg = (img) => {
    if (loading || !profileImage) return;
    registerCachedImage(img, {
      onLoaded: () => setLoadedProfileImage(profileImage),
      onFailed: () => handleImageError?.(),
    });
  };

  // A hung response fires neither onLoad nor onError, which would pin the
  // skeleton forever — after the timeout, fail the candidate so the fallback
  // queue advances.
  useEffect(() => {
    if (loading || !profileImage || imageLoaded) return undefined;
    const timer = setTimeout(() => handleImageError?.(), IMAGE_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [loading, profileImage, imageLoaded, handleImageError]);

  const { data: surface } = useQuery(
    [profileImage, "bgGradient"],
    async () => {
      const { dark, vibrant, ink } = await extractImageColors(profileImage);
      return { raw: [vibrant, dark], ink };
    },
    // Palette work is decorative. Start it only after the real image can paint
    // so it never delays the thumbnail's first useful frame.
    { enabled: imageLoaded }
  );

  const coversTheRamp = fill || backgroundFullHeight;
  const { bgGradient, filterImg } = useMemo(
    () => eventSurface({ surface, coversTheRamp, grayscale }),
    [surface, coversTheRamp, grayscale]
  );
  const imgStyle = fill
    ? { width: "100%", height: "100%", objectFit: "cover", display: "block" }
    : {
        ...(backgroundFullHeight
          ? {
              height: "100%",
              width: "50%",
              objectFit: "cover",
              objectPosition: "top",
            }
          : backgroundMidHeight
          ? {
              width: "auto",
              height: "80%",
              minWidth: "50%",
              objectFit: "contain",
            }
          : {
              width: "auto",
              height: "70px",
              minWidth: "70px",
              objectFit: "contain",
            }),
        filter: filterImg,
      };

  const showImage = !loading && !!profileImage && imageLoaded;

  return (
    <>
      {!showImage ? (
        <Skeleton
          id="img-loader"
          data-testid="event-profile-art-skeleton"
          animation="wave"
          variant="rectangular"
          sx={{ borderRadius: "4px", ...sx }}
          width={props.width}
          height={Number(props.height.split("px")[0])}
        />
      ) : (
        <></>
      )}
      <Box
        display={showImage ? "flex" : "none"}
        justifyContent="center"
        alignItems="center"
        bgcolor={"#f0f0f0"} // Set the background color
        borderRadius="8px" // Rounded corners
        sx={{
          background: bgGradient ?? "#f0f0f0",
          maxWidth: "-webkit-fill-available",
          position: "relative",
          overflow: "hidden",
        }}
        {...Object.fromEntries(
          Object.entries(props).filter(([key]) => key !== "calendarId")
        )}
      >
        <img
          ref={registerImg}
          src={profileImage}
          alt={eventSummary}
          decoding="async"
          onLoad={() => setLoadedProfileImage(profileImage)}
          onError={handleImageError}
          style={{ ...imgStyle }}
        />
      </Box>
    </>
  );
};

export default EventProfileBg;
